import Button from '@/components/Buttons/Button';
import SingUpModal from '@/components/Modals/SingUpModal';
import { toast } from '@/components/toast';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { activePlayerIdAtom } from '@/hooks/useAudioPlayer';
import { MAX_SCENE_COUNT, MAX_SCENE_DURATION_IN_MS, MAX_VIDEO_DURATION_IN_MS } from '@/pages/AdvancedEditor/constants';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { getPageProperty, PolotnoStoreActivePageKeys, usePolotnoActivePageState } from '@/pages/AdvancedEditor/hooks';
import { useUpdateDuration } from '@/pages/AdvancedEditor/hooks/useUpdateDuration';
import { validateTtsText } from '@/pages/AdvancedEditor/utils';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { creditsToVideoMinutes } from '@/state/credits/credits';
import { ttsGenerationErrors } from '@/state/presenters';
import theme from '@/theme';
import { shakeAnimation } from '@/utils/utils';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { SIDEBAR_PANELS } from '../../../../types';
import { GenerateDropdown } from '../GenerateDropdown';
import { getGenerationValidationError } from './generationValidator';

const GenerateButtonContainer = styled.div`
    position: absolute;
    z-index: 1;
    right: 30px;
    top: 12px;
`;

const componentsProps = {
    popper: {
        disablePortal: true,
        sx: {
            marginTop: '1.5rem !important',
            transform: 'translate(2rem, 3rem) !important',
            '& .MuiTooltip-tooltip': {
                background: 'transparent',
                color: 'black',
                padding: '0.5rem',
                maxWidth: '33.5rem',
                borderRadius: '8px',
            },
        },
    },
};

const StyledGenerateButton = styled(Button, { shouldForwardProp: p => !['shake', 'shouldDisplayPopup', 'active'].includes(p) })<{
    shake?: boolean;
    shouldDisplayPopup?: boolean;
    active?: boolean;
}>(({ shake, shouldDisplayPopup, active }) => ({
    animation: shake ? `${shakeAnimation} 0.5s` : 'none',
    animationIterationCount: 4,
    animationDelay: '4s',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '18px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    gap: '8px',
    borderRadius: '8px',
    lineHeight: '28px',
    height: '48px',
    position: 'relative',
    ...(active && {
        '&.MuiButtonBase-root.MuiButton-root.MuiButton-contained': {
            background: theme.colors.primary.hover,
        },
    }),
    ...(shouldDisplayPopup && {
        cursor: 'not-allowed',
        '&.MuiButton-contained:hover': {
            background: theme.colors.primary.main + ' !important',
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: 'calc(20px + 100%)',
            bottom: '-20px',
            left: '0',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: '0',
            left: '0',
            background: '#fff',
            opacity: 0.5,
            borderRadius: '8px',
        },
    }),
}));

interface Props {
    onCtaClick: (onSuccess?: () => void) => void;
    onOpen: () => void;
    isProcessing: boolean;
}

export const GenerateButton = observer(({ onCtaClick, onOpen, isProcessing }: Props) => {
    const { isAnonymous } = useAuth();
    const { store, setActivePanel } = useAdvancedEditorContext();

    const [ttsError, setTtsError] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsError);
    const setActivePlayerId = useSetRecoilState(activePlayerIdAtom);
    const { updateDuration, durationUpdated, videoDuration } = useUpdateDuration();
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);
    const errorMessage = getGenerationValidationError(store);

    const [open, setOpen] = useState(false);
    const [showUpgradeModal, setShowSignupModal] = useState(false);
    const [_1, setButtonAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [_2, setMouseOnButton] = useState(false);

    const pagesWithTextError = store.pages.filter(
        page =>
            validateTtsText(
                getPageProperty(page, PolotnoStoreActivePageKeys.textInput),
                getPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedVoice)
            ).textTooLong
    );

    const validateLimits = useCallback((): boolean => {
        if (store.pages.length > MAX_SCENE_COUNT) {
            toast.info({ title: `You’ve exceeded the ${MAX_SCENE_COUNT}-scene per video limit`, message: 'Please adjust to continue.' });
            return false;
        }

        const durationCalculatedForAll = store.pages.every(p => getPageProperty(p, PolotnoStoreActivePageKeys.sceneDurationCalculated));
        const somePageExcessDuration = store.pages.some(p => p.duration > MAX_SCENE_DURATION_IN_MS);
        if (durationCalculatedForAll && somePageExcessDuration) {
            toast.info({
                title: `One or more scenes exceed our maximum of ${Math.round(MAX_SCENE_DURATION_IN_MS / (60 * 1000))} minutes.`,
                message: 'Please adjust to continue.',
            });
            return false;
        }

        if (videoDuration && Math.round(videoDuration * 1000) > MAX_VIDEO_DURATION_IN_MS) {
            toast.error({
                title: `Your video exceeds our ${Math.round(MAX_VIDEO_DURATION_IN_MS / (60 * 1000))}-minute duration limit. Please adjust to continue.`,
            });
            return false;
        }
        return true;
    }, [videoDuration]);

    useEffect(() => {
        if (open) {
            const isValid = validateLimits();
            if (!isValid) {
                setOpen(false);
            }
        }
    }, [open, durationUpdated, videoDuration]);

    useEffect(() => {
        if (durationUpdated || !open) {
            return;
        }

        const updateProjectDuration = async () => {
            try {
                await updateDuration();
            } catch (e) {
                const pageWithTtsError = store.pages.find(page => !!getPageProperty(page, PolotnoStoreActivePageKeys.ttsError));

                if (pageWithTtsError) {
                    setActivePanel(SIDEBAR_PANELS.SCRIPT);
                    store.selectPage(pageWithTtsError.id);
                    const ttsError = getPageProperty(pageWithTtsError, PolotnoStoreActivePageKeys.ttsError)!;
                    const toastMethod = ttsError.key === 'language' ? toast.info : toast.error;
                    toastMethod({
                        title: ttsError.title,
                    });
                }

                handleClose();
            }
        };

        updateProjectDuration();
    }, [durationUpdated, open]);

    useEffect(() => {
        if (ttsError) {
            setOpen(false);
        }
    }, [ttsError]);

    const openGenerate = (newState: boolean) => {
        if (isAnonymous) {
            setShowSignupModal(true);
        } else if (pagesWithTextError.length) {
            store.selectPage(pagesWithTextError[0].id);
            setActivePanel(SIDEBAR_PANELS.SCRIPT);
            setTtsError(ttsGenerationErrors['text-limit']);
        } else {
            if (!validateLimits()) return;

            onOpen();
            setOpen(newState);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setActivePlayerId('');
    };

    return (
        <GenerateButtonContainer>
            <Tooltip
                componentsProps={componentsProps}
                onClose={handleClose}
                open={open}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                placement="bottom-end"
                title={
                    <GenerateDropdown
                        onCtaClick={onCtaClick}
                        onClickAway={handleClose}
                        duration={durationUpdated ? videoDuration : 0}
                        isProcessing={isProcessing}
                        pageIds={store.pages.map(page => page.id)}
                    />
                }>
                <span>
                    <CustomTooltip title={errorMessage} whitespace="pre">
                        <StyledGenerateButton
                            active={open}
                            data-testid="editor-generate-button"
                            onClick={() => openGenerate(!open)}
                            disabled={!!errorMessage}
                            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                setMouseOnButton(true);
                                setButtonAnchorEl(e.currentTarget);
                            }}
                            onMouseLeave={() => {
                                setMouseOnButton(false);
                            }}
                            shouldDisplayPopup={false}
                            disableRipple
                            disableTouchRipple
                            shake={false}>
                            Generate video
                        </StyledGenerateButton>
                    </CustomTooltip>
                </span>
            </Tooltip>

            <SingUpModal
                text="Sign up to create this video"
                mainText={`By signing up you will receive ${creditsToMinutesEnabled ? `${creditsToVideoMinutes(20)} minutes` : '20 credits'} you can use.`}
                from="advanced-editor-generate"
                isOpen={showUpgradeModal}
                onClose={() => setShowSignupModal(false)}
            />
        </GenerateButtonContainer>
    );
});
